import { apiService } from '../services/api';
import { AllDronesSummaryResponse, DroneDateRangeResponse, DroneSummary } from '../types/api';
import { FileFilters } from '../components/Dashboard/types/dashboard.types';
import { DashboardActions, DashboardGet, DashboardSet } from './dashboardStore.types';

type DataActions = Pick<
  DashboardActions,
  'loadInitialData' | 'refreshOverviewData' | 'loadDroneData' | 'resetDroneData'
>;

/**
 * API-integration data actions. Split out of dashboardStore.ts (STD-014) as a
 * slice factory taking the store's (set, get); behavior is unchanged.
 */
export const createDataActions = (set: DashboardSet, get: DashboardGet): DataActions => ({
  loadInitialData: async () => {
    try {
      const { ongoingRequests, setHasAttemptedLoad } = get();
      const requestKey = 'initial-all';

      if (ongoingRequests.has(requestKey)) {
        return;
      }

      ongoingRequests.add(requestKey);
      set({ loading: true, error: null }, false, 'loadInitialData-start');

      try {
        // Fetch all drones and stats in parallel
        const [allDronesData, stats] = await Promise.all([
          apiService.getAllDronesSimple(),
          apiService.getStats(undefined, undefined, undefined),
        ]);

        // Derive serial numbers and build summary from drone data
        const serialNumbers = allDronesData.map((d) => d.serial_number);
        const allDronesSummary: AllDronesSummaryResponse = {
          drones: allDronesData.map(
            (d): DroneSummary => ({
              serial_number: d.serial_number,
              model: d.model,
              total_engine_hours: d.total_engine_hours,
              total_flight_hours: d.total_flight_hours,
              next_maintenance_hours: d.next_maintenance_hours,
              overdue_hours: d.overdue_hours,
              status: d.status,
              picture_url: d.picture_url,
              legacy_hours: d.legacy_hours,
              organization_name: d.organization_name,
              ownership_type: d.ownership_type,
              lease_end_date: d.lease_end_date,
            })
          ),
        };

        set(
          (state) => ({
            data: {
              ...state.data,
              serialNumbers,
              stats,
              allDronesSummary,
            },
            loading: false,
            error: null,
          }),
          false,
          'loadInitialData-success'
        );

        setHasAttemptedLoad(true);
      } catch (error) {
        const errorMessage = 'Failed to load initial data';
        console.error('Dashboard Store:', errorMessage, error);
        set(
          {
            loading: false,
            error: errorMessage,
          },
          false,
          'loadInitialData-error'
        );

        setHasAttemptedLoad(true);
      } finally {
        ongoingRequests.delete(requestKey);
      }
    } catch (outerError) {
      console.error('Dashboard Store: Error in loadInitialData:', outerError);
      set(
        {
          loading: false,
          error: 'Failed to initialize data loading',
        },
        false,
        'loadInitialData-outer-error'
      );
    }
  },

  refreshOverviewData: async () => {
    const { ongoingRequests } = get();
    const requestKey = 'refresh-overview';

    if (ongoingRequests.has(requestKey)) {
      return;
    }

    ongoingRequests.add(requestKey);
    set({ updating: true, error: null }, false, 'refreshOverviewData-start');

    try {
      // Fetch fresh drone data
      const allDronesData = await apiService.getAllDronesSimple();

      // Build summary from drone data
      const allDronesSummary: AllDronesSummaryResponse = {
        drones: allDronesData.map(
          (d): DroneSummary => ({
            serial_number: d.serial_number,
            model: d.model,
            total_engine_hours: d.total_engine_hours,
            total_flight_hours: d.total_flight_hours,
            next_maintenance_hours: d.next_maintenance_hours,
            overdue_hours: d.overdue_hours,
            status: d.status,
            picture_url: d.picture_url,
            legacy_hours: d.legacy_hours,
            organization_name: d.organization_name,
            ownership_type: d.ownership_type,
            lease_end_date: d.lease_end_date,
          })
        ),
      };

      set(
        (state) => ({
          data: {
            ...state.data,
            allDronesSummary,
          },
          updating: false,
          error: null,
        }),
        false,
        'refreshOverviewData-success'
      );
    } catch (error) {
      const errorMessage = 'Failed to refresh overview data';
      console.error('Dashboard Store:', errorMessage, error);
      set(
        {
          updating: false,
          error: errorMessage,
        },
        false,
        'refreshOverviewData-error'
      );
    } finally {
      ongoingRequests.delete(requestKey);
    }
  },

  loadDroneData: async (
    serial: string,
    startDate?: string,
    endDate?: string,
    flightType?: string
  ) => {
    const { ongoingRequests } = get();
    const requestKey = `drone-${serial}-${startDate || 'none'}-${endDate || 'none'}-${
      flightType || 'all'
    }`;

    if (ongoingRequests.has(requestKey)) {
      return;
    }

    ongoingRequests.add(requestKey);
    set({ updating: true, error: null }, false, 'loadDroneData-start');

    try {
      const [droneStats, notesDateRange] = await Promise.all([
        apiService.getDroneStats(serial, startDate, endDate, flightType),
        apiService.getNotesDateRange(serial),
      ]);

      // Extract date range from droneStats (merged endpoint)
      const flightDateRange: DroneDateRangeResponse = droneStats.date_range ?? {
        earliest_date: null,
        latest_date: null,
        flight_dates: [],
      };

      // Combine flight dates and notes dates
      let combinedEarliestDate = flightDateRange.earliest_date;
      let combinedLatestDate = flightDateRange.latest_date;

      if (notesDateRange.earliest_date) {
        if (!combinedEarliestDate || notesDateRange.earliest_date < combinedEarliestDate) {
          combinedEarliestDate = notesDateRange.earliest_date;
        }
      }

      if (notesDateRange.latest_date) {
        if (!combinedLatestDate || notesDateRange.latest_date > combinedLatestDate) {
          combinedLatestDate = notesDateRange.latest_date;
        }
      }

      const expandedDateRange: DroneDateRangeResponse = {
        ...flightDateRange,
        earliest_date: combinedEarliestDate,
        latest_date: combinedLatestDate,
      };

      set(
        (state) => ({
          data: {
            ...state.data,
            droneStats,
            dateRange: expandedDateRange,
          },
          loading: false,
          updating: false,
          error: null,
        }),
        false,
        'loadDroneData-success'
      );
    } catch (error) {
      const errorMessage = `Failed to load data for ${serial}`;
      console.error('Dashboard Store:', errorMessage, error);
      set(
        {
          loading: false,
          updating: false,
          error: errorMessage,
        },
        false,
        'loadDroneData-error'
      );
    } finally {
      ongoingRequests.delete(requestKey);
    }
  },

  resetDroneData: () => {
    const { ongoingRequests } = get();
    ongoingRequests.clear(); // Clear pending request locks

    const defaultFileFilters: FileFilters = {
      selectedSerial: 'All',
      startDate: null,
      endDate: null,
      sortBy: 'name',
      sortOrder: 'asc',
      includeUntagged: false,
    };

    set(
      (state) => ({
        ...state,
        data: {
          flightTypes: [],
          serialNumbers: [],
          stats: null,
          droneStats: null,
          dateRange: null,
          allDronesSummary: null,
        },
        fileFilters: defaultFileFilters,
        error: null,
        hasAttemptedLoad: false, // Force reload of initial data
      }),
      false,
      'resetDroneData'
    );
  },
});
